/**
 * Settings Route (Story 1.9)
 *
 * User settings and profile management.
 * Provides navigation to different settings sections.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@provenleap/ui";
import { User, Key, Users, Tag, CreditCard, Bell, Scales, Palette, PaintBrush, Brain } from "@phosphor-icons/react";
import { usePermission, Actions } from "../../../hooks/usePermission";
import { mcpConnectUiEnabled } from "../../../lib/mcp/connect-flag";

export const Route = createFileRoute("/_authenticated/settings/")({
  component: SettingsPage,
});

function SettingsPage() {
  const { hasPermission } = usePermission();

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <ReportBreadcrumb items={[{ label: "Settings" }]} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Profile Settings — all roles */}
        <Link to="/settings/profile" className="block">
          <Card className="!rounded-[22px] shadow-xs transition-all cursor-pointer h-[120px] border-[0.5px] border-[var(--hairline)] hover:-translate-y-0.5 hover:shadow-sm hover:border-[0.75px] hover:border-[hsl(214_25%_50%)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 mb-2">
                <User size={20} />
                Profile
              </CardTitle>
              <CardDescription style={{ color: "#5A7396" }}>
                Manage your profile information, display name, and avatar
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        {/* Themes — all roles */}
        <Link to="/settings/themes" className="block">
          <Card className="!rounded-[22px] shadow-xs transition-all cursor-pointer h-[120px] border-[0.5px] border-[var(--hairline)] hover:-translate-y-0.5 hover:shadow-sm hover:border-[0.75px] hover:border-[hsl(214_25%_50%)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 mb-2">
                <Palette size={20} />
                Themes
              </CardTitle>
              <CardDescription style={{ color: "#5A7396" }}>
                Customize the look and feel with MonkeyType-inspired themes
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        {/* AI Access — gated behind the MCP connect-UI interlock (Story 29-17): the nav
            card stays HIDDEN until VITE_MCP_CONNECT_UI_ENABLED=true, matching the
            beforeLoad redirect on /settings/ai-access (otherwise the card dead-ends to a
            redirect AND reveals the unlaunched feature). */}
        {mcpConnectUiEnabled() && (
          <Link to="/settings/ai-access" className="block">
            <Card className="!rounded-[22px] shadow-xs transition-all cursor-pointer h-[120px] border-[0.5px] border-[var(--hairline)] hover:-translate-y-0.5 hover:shadow-sm hover:border-[0.75px] hover:border-[hsl(214_25%_50%)]" data-testid="settings-ai-access">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 mb-2">
                  <Brain size={20} />
                  AI Access
                </CardTitle>
                <CardDescription style={{ color: "#5A7396" }}>
                  Connect ProvenLeap to Claude, ChatGPT, and other AI assistants
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>
        )}

        {/* Security Settings — all roles (own account) */}
        <Link to="/settings/security" className="block">
          <Card className="!rounded-[22px] shadow-xs transition-all cursor-pointer h-[120px] border-[0.5px] border-[var(--hairline)] hover:-translate-y-0.5 hover:shadow-sm hover:border-[0.75px] hover:border-[hsl(214_25%_50%)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 mb-2">
                <Key size={20} />
                Security
              </CardTitle>
              <CardDescription style={{ color: "#5A7396" }}>
                Password, two-factor authentication, and account deletion
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        {/* Team Settings — Owner/Admin (Story 1.13) */}
        {hasPermission(Actions.MANAGE_TEAM) && (
          <Link to="/settings/team" className="block">
            <Card className="!rounded-[22px] shadow-xs transition-all cursor-pointer h-[120px] border-[0.5px] border-[var(--hairline)] hover:-translate-y-0.5 hover:shadow-sm hover:border-[0.75px] hover:border-[hsl(214_25%_50%)]" data-testid="settings-team">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 mb-2">
                  <Users size={20} />
                  Team
                </CardTitle>
                <CardDescription style={{ color: "#5A7396" }}>
                  Invite team members and manage roles
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>
        )}

        {/* Tags Settings — Owner/Admin (Story 3.6) */}
        {hasPermission(Actions.MANAGE_CLIENTS) && (
          <Link to="/settings/tags" className="block">
            <Card className="!rounded-[22px] shadow-xs transition-all cursor-pointer h-[120px] border-[0.5px] border-[var(--hairline)] hover:-translate-y-0.5 hover:shadow-sm hover:border-[0.75px] hover:border-[hsl(214_25%_50%)]" data-testid="settings-tags">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 mb-2">
                  <Tag size={20} />
                  Tags
                </CardTitle>
                <CardDescription style={{ color: "#5A7396" }}>
                  Create and manage tags for organizing clients
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>
        )}

        {/* Branding Settings — Owner/Admin (Story 14.5) */}
        {hasPermission(Actions.MANAGE_TENANT) && (
          <Link to="/settings/branding" className="block">
            <Card
              className="!rounded-[22px] shadow-xs transition-all cursor-pointer h-[120px] border-[0.5px] border-[var(--hairline)] hover:-translate-y-0.5 hover:shadow-sm hover:border-[0.75px] hover:border-[hsl(214_25%_50%)]"
              data-testid="settings-branding"
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2 mb-2">
                  <PaintBrush size={20} />
                  Branding
                </CardTitle>
                <CardDescription style={{ color: "#5A7396" }}>
                  Upload your logo, set brand colors, and customize how clients see your portal.
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>
        )}

        {/* Billing Settings — Owner only (Story 2.3) */}
        {hasPermission(Actions.MANAGE_BILLING) && (
          <Link to="/settings/billing" className="block">
            <Card className="!rounded-[22px] shadow-xs transition-all cursor-pointer h-[120px] border-[0.5px] border-[var(--hairline)] hover:-translate-y-0.5 hover:shadow-sm hover:border-[0.75px] hover:border-[hsl(214_25%_50%)]" data-testid="settings-billing">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 mb-2">
                  <CreditCard size={20} />
                  Billing
                </CardTitle>
                <CardDescription style={{ color: "#5A7396" }}>
                  Subscription plan, payment methods, and invoices
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>
        )}

        {/* Notifications (coming soon) */}
        <Card className="opacity-50 !rounded-[22px] shadow-xs h-[120px]" style={{ border: "0.5px solid var(--hairline)" }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 mb-2">
              <Bell size={20} />
              Notifications
            </CardTitle>
            <CardDescription style={{ color: "#5A7396" }}>
              Email and in-app notification preferences
              <span className="ml-2 text-xs bg-muted px-2 py-0.5 rounded">Coming soon</span>
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Legal */}
        <Link to="/privacy" className="block">
          <Card className="!rounded-[22px] shadow-xs transition-all cursor-pointer h-[120px] border-[0.5px] border-[var(--hairline)] hover:-translate-y-0.5 hover:shadow-sm hover:border-[0.75px] hover:border-[hsl(214_25%_50%)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 mb-2">
                <Scales size={20} />
                Legal
              </CardTitle>
              <CardDescription style={{ color: "#5A7396" }}>
                Privacy policy, terms of service, and legal information
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>
      </div>
    </div>
  );
}